package com.dotpx.ambient

import kotlin.math.PI
import kotlin.math.exp
import kotlin.math.max
import kotlin.math.min

/** Stateful materials shared by scene playback and solo previews. No allocations in nextSample. */
internal class AdditionalTextureState(private val sampleRate: Double) {
    private val random = SplitMix64(0uL)
    private val carrier = RecursiveSineOscillator(sampleRate)
    private val overtone = RecursiveSineOscillator(sampleRate)
    private val pulse = InterpolatedSineControl(sampleRate)
    private val gust = InterpolatedSineControl(sampleRate)
    private val fastAlpha = alpha(3_200.0)
    private val midAlpha = alpha(700.0)
    private val lowAlpha = alpha(110.0)
    private val deepAlpha = alpha(28.0)
    private val shortRelease = exp(-1.0 / (sampleRate * 0.018)).toFloat()
    private val brownAlpha = alpha(18.0)
    private val brownDcAlpha = alpha(5.0)
    private val brownSmoothAlpha = alpha(900.0)
    private var brown = 0f
    private var brownDc = 0f
    private var brownSmooth = 0f
    private var fast = 0f
    private var mid = 0f
    private var low = 0f
    private var deep = 0f
    private var grain = 0f
    private var controlFrames = 0
    private var framesUntilGrain = 1
    private var passPhase = 0.0
    private var passStep = 0.0
    private var passLevel = 0.0f
    private var kind = TextureKind.Brook

    fun reset(kind: TextureKind, seed: ULong) {
        this.kind = kind
        random.reset(seed xor 0x6935_FA69_6E6D_8A17uL)
        brown = 0f
        brownDc = 0f
        brownSmooth = 0f
        fast = 0f
        mid = 0f
        low = 0f
        deep = 0f
        grain = 0f
        controlFrames = 0
        framesUntilGrain = 1
        passPhase = random.nextUnitDouble()
        passStep = 1.0 / (sampleRate * (8.0 + random.nextUnitDouble() * 12.0))
        passLevel = 0.5f + random.nextUnitFloat() * 0.5f
        val frequency = when (kind) {
            TextureKind.Ventilation -> 58.0 + random.nextUnitDouble() * 8.0
            TextureKind.BoatEngine -> 34.0 + random.nextUnitDouble() * 8.0
            TextureKind.Blizzard -> 320.0 + random.nextUnitDouble() * 110.0
            else -> 480.0 + random.nextUnitDouble() * 350.0
        }
        carrier.reset(random.nextUnitDouble() * PI * 2.0, frequency)
        overtone.reset(random.nextUnitDouble() * PI * 2.0, frequency * 2.13)
        pulse.reset(random.nextUnitDouble() * PI * 2.0, if (kind == TextureKind.BoatEngine) 7.3 else 4.7)
        gust.reset(random.nextUnitDouble() * PI * 2.0, 0.037 + random.nextUnitDouble() * 0.055)
    }

    fun nextSample(noise: Float, motion: Float): Float {
        if (kind == TextureKind.BrownNoise) {
            // Leaky integration gives a brown spectrum; remove subsonic drift and soften highs.
            brown += brownAlpha * (noise - brown)
            brownDc += brownDcAlpha * (brown - brownDc)
            brownSmooth += brownSmoothAlpha * (brown - brownDc - brownSmooth)
            return brownSmooth * 5f
        }
        fast += fastAlpha * (noise - fast)
        mid += midAlpha * (noise - mid)
        low += lowAlpha * (noise - low)
        deep += deepAlpha * (noise - deep)
        val band = fast - mid
        val bass = low - deep
        val breeze = (gust.next() + 1f) * 0.5f
        val flutter = (pulse.next() + 1f) * 0.5f
        grain *= shortRelease
        framesUntilGrain -= 1
        if (framesUntilGrain <= 0) triggerGrain()

        if (controlFrames == 0) {
            when (kind) {
                TextureKind.Brook -> carrier.setFrequency(480.0 + breeze * 1_100.0 + grain * 260.0)
                TextureKind.Blizzard -> carrier.setFrequency(310.0 + breeze * 140.0 + motion * 35.0)
                else -> Unit
            }
        }
        controlFrames = (controlFrames + 1) and 63
        val fundamental = carrier.next()
        val harmonic = overtone.next()

        return when (kind) {
            // Overlapping turbulence and short resonant gurgles.
            TextureKind.Brook -> (mid * (0.9f + motion * 0.5f) +
                band * (0.18f + breeze * 0.22f) + fundamental * grain * 0.28f) * 0.6f
            TextureKind.Waterfall -> (fast * 0.72f + bass * 1.1f +
                (noise - fast) * (0.06f + breeze * 0.04f) + mid * motion * 0.24f) * 0.6f
            TextureKind.Blizzard -> (bass * (1.1f + breeze * 2.0f) +
                band * breeze * breeze * 0.28f + fundamental * breeze * breeze * 0.025f) * 0.8f
            TextureKind.Ventilation -> bass * 0.72f + mid * 0.30f +
                (fundamental * 0.13f + harmonic * 0.025f) * (0.80f + flutter * 0.20f)
            TextureKind.DistantTraffic -> {
                passPhase += passStep
                if (passPhase >= 1.0) {
                    passPhase -= 1.0
                    passStep = 1.0 / (sampleRate * (8.0 + random.nextUnitDouble() * 15.0))
                    passLevel = 0.45f + random.nextUnitFloat() * 0.55f
                }
                // Smooth rise and fall, with no seam when a new vehicle enters.
                val pass = (4.0 * passPhase * (1.0 - passPhase)).toFloat()
                val approach = pass * pass * passLevel
                bass * (0.8f + approach * 2.4f) + mid * approach * 0.46f + band * approach * 0.07f
            }
            TextureKind.BoatEngine -> {
                val combustion = 0.35f + flutter * flutter * 0.65f
                (fundamental * 0.20f + harmonic * 0.065f + bass * 0.9f) * combustion +
                    band * grain * 0.12f + mid * 0.15f
            }
            else -> 0f
        }
    }

    private fun triggerGrain() {
        val spacing = when (kind) {
            TextureKind.Brook -> 0.025 + random.nextUnitDouble() * 0.14
            TextureKind.BoatEngine -> 0.08 + random.nextUnitDouble() * 0.06
            else -> 0.2 + random.nextUnitDouble() * 0.6
        }
        framesUntilGrain = max((spacing * sampleRate).toInt(), 1)
        grain = min(grain + 0.08f + random.nextUnitFloat() * 0.55f, 1f)
    }

    private fun alpha(hertz: Double): Float =
        (1.0 - exp(-2.0 * PI * min(hertz, sampleRate * 0.45) / sampleRate)).toFloat()
}
